import React, { useEffect, useState } from 'react';
import {
  Toggle,
  Alert,
  Button,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { useDrawer } from '../../../hooks/use-drawer';
import { useAddress } from '../../../hooks/use-address';
import { useInstantCheckoutStatus } from '../../../hooks/use-instant-checkout-status';
import {
  ADD_DELIVERY_ADDRESS,
  ADD_PAYMENT,
  CONFIRM_PAYMENT,
  RE_ENROLL,
  SELECT_DELIVERY_ADDRESS,
  SELECT_PAYMENT,
  SETUP_HOW_YOU_PAY,
} from '../../../models/views';
import { TAlertStatus, TAlertType } from '../../../models/common/alert';
import { InstantCheckoutAlert } from '../../shared/InstantCheckoutAlert';
import { InstantCheckoutDrawer } from '../../InstantCheckoutDrawer/InstantCheckoutDrawer';
import { usePayments } from '../../../hooks/use-payments';

import { getChannelID, redirectToSignIn } from '../../../utils/general';
import { updateB2cProfile } from '../../../models/graphQLModels';
import { IProfile } from '../../../models/profile';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../utils/newrelic';
import { RecaptchaAction } from '../../../constants/recaptcha';
import { track } from '../../../analytics/analytics';
import { INSTANT_CHECKOUT_POST_PAGE_ERROR } from '../../../constants/analytics';
import { getIsAuthError } from '../../../constants/error-codes';
import { ERR_REDIRECT } from '../../../constants/response-error-messages';

export const InstantCheckoutToggle = ({
  getRecaptchaToken,
}: {
  getRecaptchaToken: (action: RecaptchaAction) => Promise<string>;
}) => {
  const [isCreditCardWarningVisible, setIsCreditCardWarningVisible] =
    useState<boolean>(false);
  const [toggleValue, setToggleValue] = useState<string>('PENDING');
  const {
    alertMessage,
    profileLoading,
    isEnrolledInInstantCheckout,
    userId,
    customerAccountId,
    profile,
    setUpdateProfileLoading,
    setProfile,
    setAlertMessage,
    setTimesLoaded,
  } = useInstantCheckoutStatus();
  const { hasAddresses, addressLoading, defaultAddress } = useAddress();
  const { paymentsLoading, defaultPayment, hasPayments } = usePayments();
  const { openDrawer, hideDrawer } = useDrawer();

  const [updatedProfile, setUpdatedProfile] = useState<IProfile | null>(
    profile,
  );
  const [showSetupBtn, setShowSetupBtn] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const actions = createNewRelicActions('InstantCheckoutToggle');

  useEffect(() => {
    if (profile?.userProfile) {
      const newUpdatedProfile = {
        ...profile,
        userProfile: {
          ...profile.userProfile,
          isEnrolledInInstantCheckout: false,
        },
      };
      setUpdatedProfile(newUpdatedProfile);
    }
  }, [profile]);

  const buttonStyles = 'sui-w-full md:sui-w-1/2 xl:sui-w-1/4';

  const setupBtn = async (newValue: string): Promise<string> => {
    const initialView =
      hasAddresses() === true ? SELECT_DELIVERY_ADDRESS : ADD_DELIVERY_ADDRESS;
    // eslint-disable-next-line no-console

    openDrawer(InstantCheckoutDrawer, {
      hideDrawer,
      initialView,
      flowName: SETUP_HOW_YOU_PAY,
    });
    return newValue;
  };

  const [makeUpdateCall, data] = useLazyDataModel('updateB2cProfile');

  const {
    called,
    loading: updateLoading,
    error: updateError,
    data: updateResult,
  } = data;

  const defaultPageAnalyticsPayload = {
    pageSection: 'instant checkout enrolled user summary',
    message: 'you havent saved an address or credit card',
    code: 'n/a',
    type: 'advisory',
    displayType: 'inline',
  };

  useEffect(() => {
    if (called && !updateLoading) {
      setTimesLoaded((prevState) => prevState + 1);
    }
  }, [called, updateLoading]);

  useEffect(() => {
    setUpdateProfileLoading(updateLoading);

    if (called && !updateLoading && !updateError) {
      logNewRelicAction(actions.UPDATE_PROFILE);
      const returnedProfile: IProfile = {
        customerAccountId: updateResult?.updateB2cProfile?.customerAccountId,
        userProfile: {
          isEnrolledInInstantCheckout:
            updateResult?.updateB2cProfile?.userProfile
              ?.isEnrolledInInstantCheckout,
          lastModifiedDate:
            updateResult?.updateB2cProfile?.userProfile?.lastModifiedDate,
          name: {
            firstName:
              updateResult?.updateB2cProfile?.userProfile?.name?.firstName,
            lastName:
              updateResult?.updateB2cProfile?.userProfile?.name?.lastName,
          },
          postalAddress: {
            zipCode:
              updateResult?.updateB2cProfile?.userProfile?.postalAddress
                ?.zipCode,
          },
        },
        lastModifiedDate: updateResult?.updateB2cProfile?.lastModifiedDate,
      };
      if (!updateResult?.updateB2cProfile?.userProfile?.name) {
        delete returnedProfile.userProfile.name;
      }
      if (!updateResult?.updateB2cProfile?.userProfile?.postalAddress) {
        delete returnedProfile.userProfile.postalAddress;
      }
      setProfile(returnedProfile);
    }
    if (updateError) {
      if (getIsAuthError(updateError)) {
        logNewRelicAction(actions.UPDATE_PROFILE, updateError);
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }
    }
  }, [
    called,
    updateLoading,
    updateResult,
    setUpdateProfileLoading,
    setProfile,
    updateError,
    setAlertMessage,
    actions.UPDATE_PROFILE,
  ]);

  useEffect(() => {
    if (!profileLoading && !addressLoading && !paymentsLoading) {
      setToggleValue(isEnrolledInInstantCheckout ? 'Y' : 'N');

      setShowSetupBtn(!defaultAddress && !defaultPayment);
      if (!defaultAddress || !defaultPayment) {
        setIsCreditCardWarningVisible(true);
        if (!defaultAddress && !defaultPayment) {
          track(INSTANT_CHECKOUT_POST_PAGE_ERROR, {
            ...defaultPageAnalyticsPayload,
            message: 'you havent saved a payment and address',
          });
          setWarningMessage(
            'You haven’t saved an address or credit card. Update your setting before shopping with Instant Checkout again.',
          );
        } else if (!defaultAddress) {
          track(INSTANT_CHECKOUT_POST_PAGE_ERROR, {
            ...defaultPageAnalyticsPayload,
            message: 'you havent saved an address',
          });
          setWarningMessage(
            'You haven’t saved an address. Set up where to get items before shopping with Instant Checkout again.',
          );
        } else {
          track(INSTANT_CHECKOUT_POST_PAGE_ERROR, {
            ...defaultPageAnalyticsPayload,
            message: 'you havent saved a payment',
          });
          setWarningMessage(
            'You haven’t saved a credit card. Set up how you pay before shopping with Instant Checkout again.',
          );
        }
      } else {
        setIsCreditCardWarningVisible(false);
        setWarningMessage('');
      }
    }
  }, [
    profileLoading,
    addressLoading,
    paymentsLoading,
    defaultAddress,
    defaultPayment,
    isEnrolledInInstantCheckout,
    defaultPageAnalyticsPayload,
  ]);

  const getToggleOnView = () => {
    if (defaultAddress && defaultPayment) {
      return CONFIRM_PAYMENT;
    }
    if (defaultAddress) {
      if (hasPayments()) {
        return SELECT_PAYMENT;
      }
      return ADD_PAYMENT;
    }
    if (hasAddresses()) {
      return SELECT_DELIVERY_ADDRESS;
    }
    return ADD_DELIVERY_ADDRESS;
  };

  const toggle = async () => {
    const newValue = toggleValue === 'Y' ? 'N' : 'Y';
    if (newValue === 'N') {
      setToggleValue(newValue);
      const token = await getRecaptchaToken(
        RecaptchaAction.B2C_UPDATE_PERSONAL_DETAILS,
      );
      makeUpdateCall({
        variables: {
          userId,
          customerAccountId,
          channelId: getChannelID(),
          account: updatedProfile,
        },
        ssr: false,
        context: {
          withAuth: true,
          headers: {
            'google-auth': token,
          },
        },
      });
      setAlertMessage(null);
    } else {
      openDrawer(InstantCheckoutDrawer, {
        hideDrawer,
        initialView: getToggleOnView(),
        flowName: RE_ENROLL,
      });
    }
  };

  return (
    <div>
      <div className="sui-mb-4">
        <Typography>
          Buy items in a single step. Choose Delivery when you shop online to
          purchase with Instant Checkout using your saved information.
        </Typography>
      </div>
      <div className="sui-mb-6">
        <div className="sui-col-span-full lg:sui-col-span-5 xl:sui-col-span-5 sui-flex sui-gap-8 sui-items-center">
          <Typography>Shop with Instant Checkout</Typography>

          <Toggle
            data-testid="instant-checkout-toggle-switch-btn"
            checked={toggleValue === 'Y'}
            onClick={toggle}
            inputAttributes={{ 'aria-label': 'controlled' }}
          />
        </div>
      </div>

      {alertMessage?.alertType === TAlertType.TOP && <InstantCheckoutAlert />}

      {isCreditCardWarningVisible && (
        <Alert
          status="warning"
          data-testid="instant-checkout-checkout-enabled-warning-msg"
        >
          {warningMessage}
        </Alert>
      )}
      {showSetupBtn && (
        <div className={buttonStyles}>
          <Button
            fullWidth
            data-testid="instant-checkout-set-up-btn"
            onClick={setupBtn}
          >
            Set Up Where You Get It
          </Button>
        </div>
      )}
    </div>
  );
};

InstantCheckoutToggle.dataModel = {
  updateB2cProfile,
};
